// vg-binding: P7.D-43
// vg-binding: P7.D-88
// vg-binding: P7.D-130
//
// apps/vendor/src/pages/PerformanceReports.tsx
//
// Phase 7 Wave 12 Task 43 — Performance Reports page.

import { csrfFetch } from '../lib/api/csrfFetch';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { cn, PageHeader } from '@printway/ui';

import { useTranslation } from '../lib/i18n/index.js';
import { STATUS_PILL_CLASS, NOTICE_CLASS } from '../lib/ui/statusPill.js';

interface Scorecard {
  rating_composite: number;
  sla_compliance_rate: number;
  qc_pass_rate: number;
  back_order_usage_rate: number;
  dim_accuracy_rate: number;
  out_of_band_refusal_rate: number;
  hold_period_days: 7 | 14 | 21;
}

interface ReportsResponse {
  scorecard: Scorecard;
  hold_period_days: 7 | 14 | 21;
  monthly_reports: Array<{ id: string; month: string; pdf_url: string }>;
  data_source: 'live' | 'placeholder';
}

interface ApiOk<T> { ok: true; data: T }
interface ApiErr { ok: false; error: { code: string; message: string } }

async function unwrap<T>(res: Response): Promise<T> {
  const body = (await res.json()) as ApiOk<T> | ApiErr;
  if (!body.ok) throw new Error(body.error.message);
  return body.data;
}

function useReports(): UseQueryResult<ReportsResponse, Error> {
  return useQuery({
    queryKey: ['vendor', 'performance', 'reports'],
    queryFn: async () => {
      const res = await csrfFetch('/api/v1/vendor/performance/reports', {
        credentials: 'include',
      });
      return unwrap<ReportsResponse>(res);
    },
  });
}

function fmtPct(rate: number): string {
  return `${Math.round(rate * 100)}%`;
}

function tierFor(rate: number, direction: 'higher_better' | 'lower_better'): 'good' | 'warn' | 'bad' {
  if (direction === 'higher_better') {
    if (rate >= 0.9) return 'good';
    if (rate >= 0.7) return 'warn';
    return 'bad';
  }
  if (rate <= 0.05) return 'good';
  if (rate <= 0.15) return 'warn';
  return 'bad';
}

const TIER_PILL: Record<'good' | 'warn' | 'bad' | 'empty', string> = {
  good: STATUS_PILL_CLASS.success,
  warn: STATUS_PILL_CLASS.progress,
  bad: STATUS_PILL_CLASS.danger,
  empty: 'bg-pw-surface text-pw-text-muted',
};

export function PerformanceReportsPage(): JSX.Element {
  const { t } = useTranslation('analytics');
  const q = useReports();

  if (q.isLoading) {
    return (
      <main className="px-6 py-6">
        <div className="h-64 animate-pulse rounded-xl border border-pw-border bg-pw-surface" />
      </main>
    );
  }

  if (!q.data) {
    return (
      <main className="px-6 py-6">
        <p className={`rounded-md px-3 py-2 text-sm ${NOTICE_CLASS.danger}`}>
          {t('performance.loadFailed')}
        </p>
      </main>
    );
  }

  const s = q.data.scorecard;
  const isPlaceholder = q.data.data_source === 'placeholder';
  const cells: Array<{ id: string; label: string; rate: number; dir: 'higher_better' | 'lower_better' }> = [
    { id: 'rating_composite', label: t('performance.scorecard.ratingComposite'), rate: s.rating_composite, dir: 'higher_better' },
    { id: 'sla', label: t('performance.scorecard.slaCompliance'), rate: s.sla_compliance_rate, dir: 'higher_better' },
    { id: 'qc', label: t('performance.scorecard.qcPassRate'), rate: s.qc_pass_rate, dir: 'higher_better' },
    { id: 'back_order', label: t('performance.scorecard.backOrderUsage'), rate: s.back_order_usage_rate, dir: 'lower_better' },
    { id: 'dim', label: t('performance.scorecard.dimAccuracy'), rate: s.dim_accuracy_rate, dir: 'higher_better' },
    { id: 'oob', label: t('performance.scorecard.oobRefusal'), rate: s.out_of_band_refusal_rate, dir: 'lower_better' },
  ];

  return (
    <main
      data-testid="performance-page-root"
      className="flex flex-col gap-4 px-6 py-6"
    >
      <PageHeader
        title={t('performance.pageTitle')}
        subtitle={<span className="text-xs">{t('performance.subtitle')}</span>}
      />

      {/* Scorecard grid */}
      <section
        data-testid="performance-scorecard"
        className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3"
      >
        {cells.map((c) => {
          const tier = isPlaceholder ? 'empty' : tierFor(c.rate, c.dir);
          return (
            <div
              key={c.id}
              data-testid={`scorecard-${c.id}`}
              className="rounded-xl border border-pw-border bg-pw-bg p-5 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <p className="text-sm text-pw-text-muted">
                  {c.label}{' '}
                  {/* UAT #2212 — chỉ rõ hướng đánh giá: tester thấy 0% màu
                      xanh tưởng sai, nhưng với chỉ số ↓-tốt (back-order,
                      từ chối OOB) 0% là tối ưu. */}
                  <span
                    className="text-[10px] text-pw-text-muted/70"
                    title={t(
                      c.dir === 'higher_better'
                        ? 'performance.dirHigherBetter'
                        : 'performance.dirLowerBetter',
                    )}
                  >
                    {c.dir === 'higher_better' ? '↑' : '↓'}
                  </span>
                </p>
                <span className={cn('inline-flex items-center rounded px-2 py-0.5 text-xs font-semibold', TIER_PILL[tier])}>
                  {isPlaceholder ? '—' : fmtPct(c.rate)}
                </span>
              </div>
              {isPlaceholder ? (
                <p className="mt-2 text-xs text-pw-text-muted">
                  {t('performance.noData')}
                </p>
              ) : null}
            </div>
          );
        })}
      </section>

      {/* Rating transparency — explains the hold period mapping */}
      <section
        data-testid="rating-transparency"
        className="rounded-xl border border-pw-border bg-pw-bg p-5 shadow-sm"
      >
        <h3 className="mb-3 text-sm font-semibold text-pw-text">{t('performance.holdPeriod.title')}</h3>
        <p className="text-xs text-pw-text-muted">
          {t('performance.holdPeriod.intro')}{' '}
          {isPlaceholder ? (
            <strong className="text-pw-text-muted">{t('performance.holdPeriod.currentNoData')}</strong>
          ) : (
            <strong className="text-pw-text">{t('performance.holdPeriod.currentDays', { days: q.data.hold_period_days })}</strong>
          )}
          .
        </p>
        <ul className="mt-3 flex flex-col gap-1 text-xs text-pw-text-muted">
          <li>{t('performance.holdPeriod.rule80')}</li>
          <li>{t('performance.holdPeriod.rule50')}</li>
          <li>{t('performance.holdPeriod.ruleBelow')}</li>
        </ul>
      </section>

      {/* Monthly reports — placeholder */}
      <section
        data-testid="monthly-reports"
        className="rounded-xl border border-pw-border bg-pw-bg p-5 shadow-sm"
      >
        <h3 className="mb-3 text-sm font-semibold text-pw-text">{t('performance.monthlyReports.title')}</h3>
        {q.data.monthly_reports.length === 0 ? (
          <p className="rounded-md border border-dashed border-pw-border bg-pw-surface px-3 py-6 text-center text-sm text-pw-text-muted">
            {t('performance.monthlyReports.empty')}
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {q.data.monthly_reports.map((r) => (
              <li key={r.id} className="flex items-center justify-between rounded-md border border-pw-border bg-pw-surface px-3 py-2">
                <span className="text-sm font-medium text-pw-text">{r.month}</span>
                <a
                  href={r.pdf_url}
                  className="text-sm font-semibold text-pw-primary hover:underline"
                  data-testid={`report-pdf-${r.id}`}
                >
                  {t('performance.monthlyReports.downloadLink')}
                </a>
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}

export default PerformanceReportsPage;
